import { Box, Button, Grid, Stack, TextField, Tooltip, Typography } from '@mui/material';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import useAddressInput from '../../../hooks/useAddressInput';
import { latlngType } from '../../../features/predictionMap/types';
import BasicMap from './BasicMap';
import { getAlertByAlertID, postAlert, putAlertById } from '../requests';
import { useLoaderData, useLocation, useNavigate } from 'react-router-dom';
import { alertResponseType } from '../types';
import AlertDetailSetter from './AlertDetailSetter';
import { store } from '../../../store';
import { userModelType } from '../../../features/auth/types';
import { useOtherSettings } from './useOtherSettings';
import { authType } from '../../../features/client/types';
import useFetchUser from '../../../features/client/hooks/useFetchUser';
import usePaymentModal from '../../../features/predictionMap/hooks/usePaymentModal';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { sendAnalyticsEvent } from '../../../utils/googleAnalytics';

const styles = {
  textField: {
    width: '100%',
    height: 30,
    padding: '0 16px',
  },
};

const initialLatLon = [35.6764225, 139.650027] as [number, number];

const AddAlertByAddressTab: FC = () => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const authInfo = useLoaderData() as authType;
  const { isPaid } = useFetchUser(authInfo.user.cognito_username);
  const { togglePaymentModal, PaymentModal } = usePaymentModal('alert');
  const [latLon, setLatLon] = useState<latlngType>(null);
  const [pinLatLon, setPinLatLon] = useState<latlngType>(null);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [areaName, setAreaName] = useState<string>('');
  const [defaultAddress, setDefaultAddress] = useState<string>('');
  const location = useLocation();
  const navigate = useNavigate();
  const { OtherSettingsComponents, otherSettings, setOtherSettings } = useOtherSettings();
  const is_detail = new URLSearchParams(location.search).get('is_detail');
  const {
    latlng: latLonByAddress,
    AddressInput,
    searchAddress,
  } = useAddressInput({
    placeholder: t('pages.alerts.addressSample'),
    defaultAddress: defaultAddress,
  });

  useEffect(() => {
    setPinLatLon(latLon);
  }, [latLon]);

  // only called when first rendering
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const id = queryParams.get('id');

    if (!!id) {
      getAlertByAlertID(id)
        .then(data => {
          if (!data) {
            throw new Error('');
          }
          setLatLon([data.latitude, data.longitude]);
          setDefaultAddress(data?.address || '');
          setAreaName(data.name);
        })
        .catch(d => {
        });
    }
  }, [location]);

  // 検索後にはその返り値をlatlngに格納する。
  useEffect(() => {
    if (latLonByAddress) {
      setLatLon(latLonByAddress);
    }
    setIsActive(false);
  }, [latLonByAddress]);

  useEffect(() => {
    if (!isPaid) {
      togglePaymentModal();
    }
  }, [latLon, isPaid]);

  const handleChangeLatLon = (idx: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    let newLatlng: latlngType = [0, 0];
    if (idx === 0) {
      newLatlng = [Number(e.target.value), (!!latLon && latLon[1]) || 0];
    }
    if (idx === 1) {
      newLatlng = [(!!latLon && latLon[0]) || 0, Number(e.target.value)];
    }
    setLatLon(newLatlng);
  };

  const activateInput = () => {
    // GA4: 地点アラートの緯度経度手入力開始を計測
    sendAnalyticsEvent('alert_form', {
      action: 'activate_manual_coordinate_input',
      alert_type: 'point',
    });
    setIsActive(true);
  };

  const handleChangeMapPosition = (latlng: latlngType) => {
    // GA4: 地点アラートの地図ピン移動を計測
    sendAnalyticsEvent('alert_form', {
      action: 'move_marker',
      alert_type: 'point',
    });
    setLatLon(latlng);
  };

  const handleNextClick = async () => {
    if (!latLon) {
      alert(t('pages.alerts.invalidLatLon'));
      return;
    }
    const user = (await store.getItem('user')) as userModelType;
    const data = {
      name: areaName,
      address: !!searchAddress ? searchAddress : defaultAddress,
      latitude: latLon[0],
      longitude: latLon[1],
      original_user: (!otherSettings.shareWithClient && user.id) || null,
    };
    const queryParams = new URLSearchParams(location.search);
    const id = queryParams.get('id');
    let res = false as alertResponseType | boolean;
    // idがあったらput
    if (!!id) {
      res = await putAlertById(id, data);
    } else {
      res = await postAlert(data);
    }
    if (!!res && !!res) {
      // GA4: 地点アラートの登録・更新成功を計測
      sendAnalyticsEvent('alert_form', {
        action: !!id ? 'update_success' : 'create_success',
        alert_type: 'point',
      });
      navigate(`/prediction/new_alert?id=${res.id}&is_detail=true`);
    }
    return;
  };

  const canSend = useMemo(() => {
    return !!areaName && !!latLon && !!isPaid;
  }, [areaName, latLon, isPaid]);

  if (is_detail) {
    return (
      <>
        <AlertDetailSetter />
      </>
    );
  }

  return (
    <Stack direction={isMobile ? "column" : 'row'}>
      {PaymentModal}

      <Box sx={{ width: isMobile ? '100%' : '45%' }}>
        <Box>
          <Typography style={{ fontWeight: 'bold', fontSize: '12pt' }}>
            1. {t('pages.alerts.addNewAlert.enterAddress')}
            <Tooltip title={t('pages.alerts.tooltip.addressInput')}>
              <HelpOutlineIcon style={{ cursor: 'pointer', marginLeft: 2, fontSize: '14px' }} />
            </Tooltip>
          </Typography>
          <Box style={{ textAlign: 'left', marginTop: '4px' }}>{AddressInput}</Box>

          <Grid container style={{ marginTop: '8px' }} spacing={1}>
            <Grid item xs={6}>
              <Typography
                style={{
                  fontWeight: 'bold',
                  fontSize: '10pt',
                  color: (isActive && 'black') || 'gray',
                }}
              >
                {t('pages.alerts.latitude')}
              </Typography>
              <TextField
                value={(!!pinLatLon && pinLatLon[0]) || ''}
                type="number"
                onChange={handleChangeLatLon(0)}
                inputProps={{
                  style: styles.textField,
                }}
                disabled={!isActive}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography
                style={{
                  fontWeight: 'bold',
                  fontSize: '10pt',
                  color: (isActive && 'black') || 'gray',
                }}
              >
                {t('pages.alerts.longitude')}
              </Typography>
              <TextField
                value={(!!pinLatLon && pinLatLon[1]) || ''}
                type="number"
                onChange={handleChangeLatLon(1)}
                inputProps={{
                  style: styles.textField,
                }}
                disabled={!isActive}
              />
            </Grid>
          </Grid>
          <Typography
            style={{ fontSize: '11pt', textDecoration: 'underline', cursor: 'pointer' }}
            onClick={activateInput}
          >
            {t('pages.alerts.inputManually')}
          </Typography>
        </Box>

        <Box style={{ marginTop: '32px' }}>
          <Typography style={{ fontWeight: 'bold', fontSize: '12pt' }}>
            2. {t('pages.alerts.addNewAlert.selectOnMap')}
          </Typography>
          <Box style={{ textAlign: 'left', marginTop: '4px' }}>
            <Typography style={{ fontSize: '9pt' }}>
              {t('pages.alerts.tooltip.clickOnMap')}
            </Typography>
          </Box>
        </Box>

        <Box style={{ marginTop: '32px' }}>
          <Typography style={{ fontWeight: 'bold', fontSize: '12pt' }}>
            3. {t('pages.alerts.addNewAlert.editAreaName')}
            <Tooltip title={t('pages.alerts.tooltip.areaName')}>
              <HelpOutlineIcon style={{ cursor: 'pointer', marginLeft: 2, fontSize: '14px' }} />
            </Tooltip>
          </Typography>
          <TextField
            sx={{
              '& .MuiInputBase-root': {
                backgroundColor: '#fff',
              },
            }}
            inputProps={{ style: styles.textField }}
            placeholder={t('pages.alerts.areaNameSample')}
            value={areaName}
            onChange={e => {
              setAreaName(e.target.value);
            }}
          />
        </Box>

        <OtherSettingsComponents
          otherSettings={otherSettings}
          setOtherSettings={setOtherSettings}
        />

        {/* 次へボタン */}
        <Box style={{ marginTop: '36px' }}>
          <Button
            variant="contained"
            style={canSend ? { backgroundColor: '#007B43' } : {}}
            onClick={handleNextClick}
            disabled={!(isPaid && canSend)}
          >
            {t('pages.alerts.goNext')}
          </Button>
        </Box>
      </Box>

      {/* 画面の右半分（isMobile時は上に表示） */}
      <Box
        sx={{
          width: isMobile ? '100%' : '50%',
          ...(isMobile ? {
            order: -1,
            mb: 2,
            height: 300,
            minHeight: 0,
            maxHeight: 300,
            overflow: 'hidden',
          } : { marginLeft: '5%' }),
        }}
      >
        <BasicMap
          currentMarker={!!latLon ? latLon : initialLatLon}
          onChange={handleChangeMapPosition}
        />
      </Box>
    </Stack>
  );
};

export default AddAlertByAddressTab;
